// components/ScheduledPosts.tsx
// View and manage scheduled posts with calendar

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { BarkBoardPost } from '../types';
import './ContentLibrary.css';

export const ScheduledPosts: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();

    const [posts, setPosts] = useState<BarkBoardPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date());


    const loadScheduledPosts = useCallback(async () => {
        if (!currentUser?.email) return;

        setLoading(true);
        try {
            const q = query(
                collection(db, 'barkBoard', 'posts', 'items'),
                where('status', '==', 'scheduled'),
                where('postType', '==', 'admin')
            );

            const snapshot = await getDocs(q);
            const loadedPosts = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    type: data.type,
                    title: data.title,
                    caption: data.caption,
                    category: data.category,
                    videoUrl: data.videoUrl,
                    audioUrl: data.audioUrl,
                    thumbnailUrl: data.thumbnailUrl,
                    articleContent: data.articleContent,
                    authorName: data.authorName,
                    authorEmail: data.authorEmail,
                    createdAt: data.createdAt?.toDate(),
                    publishedAt: data.publishedAt?.toDate(),
                    scheduledFor: data.scheduledFor?.toDate(),
                    status: data.status,
                    viewCount: data.viewCount,
                    complimentCount: data.complimentCount,
                } as BarkBoardPost;
            });

            setPosts(loadedPosts.sort((a, b) =>
                (a.scheduledFor?.getTime() || 0) - (b.scheduledFor?.getTime() || 0)
            ));
        } catch (err) {
            console.error('Failed to load scheduled posts:', err);
            setError('Failed to load scheduled posts');
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        loadScheduledPosts();
    }, [loadScheduledPosts]);

    const handlePublishNow = async (postId: string) => {
        if (!window.confirm('Publish this post now?')) return;

        try {
            await updateDoc(doc(db, 'barkBoard', 'posts', 'items', postId), {
                status: 'published',
                publishedAt: serverTimestamp(),
                scheduledFor: null,
            });

            setPosts(posts.filter(p => p.id !== postId));
            alert('Post published successfully!');
        } catch (err) {
            console.error('Failed to publish:', err);
            alert('Failed to publish post');
        }
    };

    const handleCancelSchedule = async (postId: string) => {
        if (!window.confirm('Cancel this scheduled post? It will be moved to drafts.')) return;

        try {
            await updateDoc(doc(db, 'barkBoard', 'posts', 'items', postId), {
                status: 'draft',
                scheduledFor: null,
            });

            setPosts(posts.filter(p => p.id !== postId));
            alert('Schedule cancelled. Post moved to drafts.');
        } catch (err) {
            console.error('Failed to cancel:', err);
            alert('Failed to cancel schedule');
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const formatDateTime = (date: Date) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const getCalendarDays = () => {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }
        return days;
    };

    const getPostsForDay = (day: number) => {
        return posts.filter(post => {
            if (!post.scheduledFor) return false;
            const postDate = new Date(post.scheduledFor);
            return postDate.getDate() === day &&
                postDate.getMonth() === selectedMonth.getMonth() &&
                postDate.getFullYear() === selectedMonth.getFullYear();
        });
    };

    const changeMonth = (delta: number) => {
        setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + delta, 1));
    };

    return (
        <div className="library-container">
            <header className="library-header">
                <div className="header-content">
                    <div className="header-left">
                        <button onClick={() => navigate('/dashboard')} className="back-button">
                            ← Back
                        </button>
                        <h1>Scheduled Posts</h1>
                    </div>
                    <div className="header-actions">
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <div className="library-content">
                {error && <div className="error-message">{error}</div>}

                {/* Calendar View */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                        <button onClick={() => changeMonth(-1)} className="cancel-button">← Prev</button>
                        <h2 style={{ margin: 0 }}>
                            {selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h2>
                        <button onClick={() => changeMonth(1)} className="cancel-button">Next →</button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} style={{ textAlign: 'center', fontWeight: 600, padding: '8px' }}>
                                {day}
                            </div>
                        ))}
                        {getCalendarDays().map((day, index) => {
                            const dayPosts = day ? getPostsForDay(day) : [];
                            return (
                                <div
                                    key={index}
                                    style={{
                                        minHeight: '80px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        padding: '8px',
                                        background: day ? 'white' : '#f7fafc',
                                    }}
                                >
                                    {day && (
                                        <>
                                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>{day}</div>
                                            {dayPosts.map(post => (
                                                <div
                                                    key={post.id}
                                                    style={{
                                                        background: '#667eea',
                                                        color: 'white',
                                                        padding: '4px',
                                                        borderRadius: '4px',
                                                        fontSize: '10px',
                                                        marginBottom: '2px',
                                                        cursor: 'pointer',
                                                    }}
                                                    title={post.title}
                                                >
                                                    {post.type === 'video' ? '🎥' : post.type === 'audio' ? '🎵' : '📝'}
                                                    {' '}
                                                    {post.scheduledFor ?
                                                        new Date(post.scheduledFor).toLocaleTimeString('en-US', {
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })
                                                        : ''
                                                    }
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* List View */}
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading scheduled posts...</p>
                    </div>
                ) : posts.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📅</div>
                        <h3>No scheduled posts</h3>
                        <p>Schedule posts to publish them automatically at a future date</p>
                        <button onClick={() => navigate('/dashboard')} className="create-button">
                            Create Content
                        </button>
                    </div>
                ) : (
                    <div className="posts-grid">
                        {posts.map(post => (
                            <div key={post.id} className="post-card">
                                <div className="post-card-content">
                                    <h3 className="post-title">{post.title}</h3>
                                    <p className="post-caption">
                                        {post.caption.substring(0, 100)}
                                        {post.caption.length > 100 ? '...' : ''}
                                    </p>
                                    <div className="post-meta">
                                        <div className="meta-item">
                                            <span className="meta-icon">
                                                {post.type === 'video' ? '🎥' :
                                                    post.type === 'audio' ? '🎵' : '📝'}
                                            </span>
                                            <span className="meta-text">{post.type}</span>
                                        </div>
                                        {post.scheduledFor && (
                                            <div className="meta-item">
                                                <span className="meta-icon">⏰</span>
                                                <span className="meta-text">{formatDateTime(post.scheduledFor)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="post-card-actions">
                                    <button
                                        onClick={() => handlePublishNow(post.id)}
                                        className="action-button edit-button"
                                    >
                                        🚀 Publish Now
                                    </button>
                                    <button
                                        onClick={() => handleCancelSchedule(post.id)}
                                        className="action-button delete-button"
                                    >
                                        ❌ Cancel
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};